import React from 'react';
import { Link } from 'react-router-dom';
import { FaBroadcastTower, FaFileAlt, FaUsers, FaSignInAlt, FaKey } from 'react-icons/fa';
import { AiOutlineFundProjectionScreen } from 'react-icons/ai';
import { IoCarSharp } from 'react-icons/io5';
import { CiViewBoard } from 'react-icons/ci';
import { FaIndianRupeeSign } from 'react-icons/fa6';
import { MdOutlineTravelExplore } from 'react-icons/md';
import { BiCommentDetail } from 'react-icons/bi';
import { TbTimelineEventText, TbMapPinOff, TbChartBar } from 'react-icons/tb';
import { FiMapPin, FiEye, FiCrosshair } from 'react-icons/fi';
import { useAuth } from '../../provider/auth.provider';
import { FaShieldAlt } from "react-icons/fa"

interface ReportCard {
  icon: React.ComponentType<any>;
  label: string;
  description: string;
  path: string;
  resource: string;
  action: string;
  color: string;
  group?: string;
}

const reportCards: ReportCard[] = [
  {
    icon: AiOutlineFundProjectionScreen,
    label: 'Project Claims',
    description: 'Project claim report with location trail and distances',
    path: '/reports/claims/project',
    resource: 'reports',
    action: 'view_project_claim_report',
    color: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
    group: 'Claim Reports',
  },
  {
    icon: IoCarSharp,
    label: 'Cab Hiring Claims',
    description: 'Cab hiring claim report with readings and distances',
    path: '/reports/claims/cab-hiring',
    resource: 'reports',
    action: 'view_cab_hiring_claim_report',
    color: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100',
    group: 'Claim Reports',
  },
  {
    icon: CiViewBoard,
    label: 'General Claims',
    description: 'General expense claim report',
    path: '/reports/claims/general',
    resource: 'reports',
    action: 'view_general_claim_report',
    color: 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100',
    group: 'Claim Reports',
  },
  {
    icon: FaIndianRupeeSign,
    label: 'Advance Claims',
    description: 'Advance payment claim report',
    path: '/reports/claims/advance',
    resource: 'reports',
    action: 'view_advance_claim_report',
    color: 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100',
    group: 'Claim Reports',
  },
  {
    icon: FaBroadcastTower,
    label: 'Site Reports',
    description: 'Site report with workflow status and filters',
    path: '/reports/site',
    resource: 'reports',
    action: 'view_site_report',
    color: 'bg-indigo-50 text-indigo-700 border-indigo-200 hover:bg-indigo-100',
    group: 'Site',
  },
  {
    icon: FaFileAlt,
    label: 'Site Distance Report (With KM)',
    description: 'Site distance report by site visit date',
    path: '/reports/site-distance',
    resource: 'reports',
    action: 'view_site_distance_report',
    color: 'bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100',
    group: 'Site',
  },
  {
    icon: FaFileAlt,
    label: 'Site Distance Report 2 (Without KM)',
    description: 'The same site distance report without road distances or System TA slabs',
    path: '/reports/site-distance-without-km',
    resource: 'reports',
    action: 'view_site_distance_report_without_km',
    color: 'bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100',
    group: 'Site',
  },
  {
    icon: FaUsers,
    label: 'Users Report',
    description: 'Users report with project assignments and designations',
    path: '/reports/users',
    resource: 'reports',
    action: 'view_users_report',
    color: 'bg-pink-50 text-pink-700 border-pink-200 hover:bg-pink-100',
    group: 'Human Resource',
  },
  {
    icon: FaSignInAlt,
    label: 'Login Report',
    description: 'User login and logout activity with session details',
    path: '/reports/login',
    resource: 'reports',
    action: 'view_login_report',
    color: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
    group: 'Human Resource',
  },
  {
    icon: FaKey,
    label: 'Password Change Report',
    description: 'Last password change details for all users',
    path: '/reports/password',
    resource: 'reports',
    action: 'view_password_report',
    color: 'bg-green-50 text-green-700 border-green-200 hover:bg-green-100',
    group: 'Human Resource',
  },
  {
    icon: FaShieldAlt,
    label: 'Permission Report',
    description: 'Role × Permission matrix showing Yes/No for each role',
    path: '/reports/permissions',
    resource: 'reports',
    action: 'view_permission_report',
    color: 'bg-purple-50 text-purple-700 border-purple-200 hover:bg-purple-100',
    group: 'Admin',
  },
  {
    icon: MdOutlineTravelExplore,
    label: 'Daily Travel Report',
    description: 'Daily employee travel sequence from check-in to checkout',
    path: '/reports/daily-travel',
    resource: 'reports',
    action: 'view_daily_travel_report',
    color: 'bg-cyan-50 text-cyan-700 border-cyan-200 hover:bg-cyan-100',
    group: 'Site',
  },
  {
    icon: TbTimelineEventText,
    label: 'Survey Workflow Report',
    description: 'Stage-wise site detail across the survey workflow, scoped to your projects',
    path: '/reports/survey-workflow',
    resource: 'reports',
    action: 'view_survey_workflow_report',
    color: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',
    group: 'Site',
  },
  {
    icon: FiMapPin,
    label: 'Site & FW Start Difference',
    description: 'Aerial distance between a site and where its field work was started',
    path: '/reports/site-fw-start',
    resource: 'reports',
    action: 'view_site_fw_start_report',
    color: 'bg-lime-50 text-lime-700 border-lime-200 hover:bg-lime-100',
    group: 'Site',
  },
  {
    icon: FiEye,
    label: 'Client View',
    description: 'All sites of your projects with field data download and slideshow',
    path: '/reports/client-view',
    resource: 'reports',
    action: 'view_client_view_report',
    color: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
    group: 'Site',
  },
  {
    icon: FiCrosshair,
    label: 'Wrong Site Location',
    description: "Engineers' reports that a site's lat/long is wrong, with their own position",
    path: '/reports/site-location',
    resource: 'reports',
    action: 'view_site_location_report',
    color: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100',
    group: 'Site',
  },
  {
    icon: TbMapPinOff,
    label: 'Reporting vs Check-in Difference',
    description: 'Aerial distance between an employee reporting location and their attendance check-in',
    path: '/reports/reporting-checkin',
    resource: 'reports',
    action: 'view_reporting_checkin_report',
    color: 'bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100',
    group: 'Human Resource',
  },
  {
    icon: TbChartBar,
    label: 'User Productivity Report',
    description: 'Work assigned to and completed by each user in a month, across all roles',
    path: '/reports/user-productivity',
    resource: 'reports',
    action: 'view_user_productivity_report',
    color: 'bg-violet-50 text-violet-700 border-violet-200 hover:bg-violet-100',
    group: 'Human Resource',
  },
  {
    icon: BiCommentDetail,
    label: 'User Feedback Report',
    description: 'TA, MIS, designer, and validator review feedback report',
    path: '/reports/user-feedback',
    resource: 'reports',
    action: 'view_site_report',
    color: 'bg-rose-50 text-rose-700 border-rose-200 hover:bg-rose-100',
    group: 'Site',
  },
];

const ReportsPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const visibleCards = reportCards.filter(card => hasPermission(card.resource, card.action));
  const groups = Array.from(new Set(visibleCards.map(card => card.group || 'General')));

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Reports</h1>
        <p className="text-sm text-gray-500 mt-1">Open reports in a focused window view</p>
      </div>

      {visibleCards.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <p>You do not have access to any report sections.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {groups.map(group => {
            const groupCards = visibleCards.filter(card => (card.group || 'General') === group);
            return (
              <div key={group}>
                <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{group}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {groupCards.map(card => (
                    <Link
                      key={card.path}
                      to={card.path}
                      className={`flex items-start gap-4 p-5 border rounded-lg transition-colors ${card.color}`}
                    >
                      <div className="mt-0.5 flex-shrink-0">
                        <card.icon className="w-6 h-6" />
                      </div>
                      <div>
                        <p className="font-semibold text-sm">{card.label}</p>
                        <p className="text-xs mt-0.5 opacity-70">{card.description}</p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
